import { useEffect, useRef, useState } from "react";

/**
 * CountUp — animato da 0 → `to` quando il componente entra nel viewport.
 *
 * Props:
 *  - to (number): valore finale
 *  - duration (ms): durata dell'animazione (default 1600)
 *  - locale (string): locale per il formatting (default "it-IT")
 *  - startOnView (boolean): avvia solo quando visibile (default true)
 *  - className: classi aggiuntive
 */
export default function CountUp({ to, duration = 1600, locale = "it-IT", startOnView = true, className = "" }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const start = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const t0 = performance.now();
      const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3);
      const step = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        setValue(Math.round(to * easeOutCubic(p)));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    if (!startOnView) {
      start();
      return;
    }

    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      start();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            start();
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration, startOnView]);

  return (
    <span ref={ref} className={className} data-testid="countup">
      {new Intl.NumberFormat(locale, { useGrouping: true, maximumFractionDigits: 0 }).format(value)}
    </span>
  );
}
